import React, { useState, useEffect, useCallback } from 'react';
import { CONTACT_EMAIL } from './contact.js';
import { navigate } from './site/routes.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

// The same two faces the rest of the site uses: Inter for anything anyone
// reads, Space Mono for uppercase letterspaced chrome and for figures. The
// page used to carry a third — Trade Gothic Compressed, which nothing loads,
// so every heading here was really Arial Narrow.
const mono = `'Space Mono', 'Courier Prime', 'Courier New', monospace`;
const sans = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;

// Aligned to the site tokens rather than approximating them, so a visitor who
// arrives here from /work does not cross a seam.
const c = {
  cream: '#fdf9f3',
  paper: '#ffffff',
  sand: '#e0d3c2',
  orange: '#b8560f',
  accent: '#d97540',
  terracotta: '#a84032',
  brown: '#2b1810',
  ink: '#2b1810',
  muted: '#6b5648',
  ground: '#2b1810',
  onGround: '#fdf9f3',
  groundMuted: '#b8a495',
  groundLine: '#4a2e1f',
};

// Inline styles can't express a media query, and the pieces that break on a
// phone are all layout: a fixed sidebar column, and a card grid whose 280px
// minimum is wider than a small screen once padding is taken off.
const RESPONSIVE_CSS = `
.pg-layout { max-width:1160px; margin:0 auto; padding:56px 24px 72px; display:grid;
  grid-template-columns:minmax(0,1fr) 260px; gap:56px; align-items:start; }
.pg-cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:36px; }
.pg-detail { max-width:820px; margin:0 auto; padding:48px 24px 72px; }
.pg-bar { max-width:1160px; margin:0 auto; padding:0 24px; display:flex; align-items:center; min-height:62px; }
.pg-mast { padding:52px 24px 36px; }
.pg-mast-title { font-size:52px; }
.pg-foot { max-width:1160px; margin:0 auto; padding:48px 24px 28px; display:grid;
  grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:28px; }
.pg-pet-name { font-size:52px; }
.pg-price { font-size:34px; }
.pg-sponsors { font-size:22px; }
@media (max-width: 900px) {
  .pg-layout { grid-template-columns:1fr; gap:40px; padding:40px 18px 56px; }
}
@media (max-width: 620px) {
  .pg-cards { grid-template-columns:1fr; gap:28px; }
  .pg-detail { padding:32px 18px 56px; }
  .pg-mast { padding:36px 18px 26px; }
  .pg-mast-title { font-size:34px; }
  .pg-bar { padding:0 18px; }
  .pg-foot { padding:36px 18px 24px; }
  .pg-pet-name { font-size:34px; }
  .pg-price { font-size:26px; }
  .pg-sponsors { font-size:18px; }
}
`;

function ResponsiveStyles() {
  return <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />;
}

// Whole dollars stay clean ($50), but a custom tip can carry cents and must
// not be rounded away — the button would promise $58 and charge $57.50.
const money = (cents) => {
  const v = (cents || 0) / 100;
  return `$${Number.isInteger(v) ? v : v.toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

// The wordmark lockup is the site's, not a second one that resembles it: dark
// ground, 2px accent rule, sans wordmark beside a mono locality. The gallery's
// own title then sits in a band below it, the way a section head does
// everywhere else.
function Masthead({ subtitle }) {
  const home = (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button === 1) return;
    ev.preventDefault();
    navigate('/');
  };
  return (
    <header>
      <ResponsiveStyles />
      <div style={{ background: c.ground, color: c.onGround, borderBottom: `2px solid ${c.accent}` }}>
        <div className="pg-bar">
          <a href="/" onClick={home} aria-label="HDDN LAB — home"
            style={{ display: 'flex', alignItems: 'baseline', gap: 8, textDecoration: 'none', color: c.onGround }}>
            <b style={{ fontFamily: sans, fontWeight: 800, fontSize: 17, letterSpacing: '0.16em', textTransform: 'uppercase' }}>HDDN LAB</b>
            <i style={{ fontStyle: 'normal', fontFamily: mono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: c.accent }}>New Rochelle</i>
          </a>
        </div>
      </div>
      <div className="pg-mast" style={{ background: c.cream, textAlign: 'center', borderBottom: `1px solid ${c.sand}` }}>
        <h1 className="pg-mast-title" style={{ fontFamily: sans, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02, color: c.ink, margin: 0 }}>
          Paws &amp; Portraits
        </h1>
        {subtitle && (
          <p style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: c.orange, margin: '16px 0 0' }}>{subtitle}</p>
        )}
      </div>
    </header>
  );
}

// Paws sits outside the site shell, so it ends with its own small version of
// the site footer rather than stopping dead after the sponsors.
function Footer() {
  const link = (href) => (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button === 1) return;
    ev.preventDefault();
    navigate(href);
  };
  const cols = [
    { head: 'Hire us', links: [['Content creation', '/content'], ['Book a photographer', '/photographer'], ['The work', '/work']] },
    { head: 'Book the room', links: [['The studio & rates', '/studio'], ['Availability', '/book']] },
  ];
  return (
    <footer style={{ background: c.ground, color: c.onGround }}>
      <div className="pg-foot">
        <div>
          <p style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Just Create</p>
          <address style={{ fontStyle: 'normal', fontFamily: sans, marginTop: 14, color: c.groundMuted, fontSize: 13, lineHeight: 1.7 }}>
            72 Centre Avenue<br />New Rochelle, NY 10801<br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.accent, textDecoration: 'none' }}>{CONTACT_EMAIL}</a>
          </address>
        </div>
        {cols.map((col) => (
          <div key={col.head}>
            <p style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: c.accent, margin: 0 }}>{col.head}</p>
            <ul style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: 9 }}>
              {col.links.map(([label, href]) => (
                <li key={href}>
                  <a href={href} onClick={link(href)} style={{ fontFamily: sans, fontSize: 13, color: c.onGround, textDecoration: 'none' }}>{label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="pg-foot" style={{ display: 'block', paddingTop: 0 }}>
        <div style={{ borderTop: `1px solid ${c.groundLine}`, paddingTop: 20, fontFamily: mono, fontSize: 11, letterSpacing: '0.08em', color: c.groundMuted }}>
          © {new Date().getFullYear()} HDDN LAB · 8 AM – 8 PM, seven days
        </div>
      </div>
    </footer>
  );
}

function Sponsors({ sponsors, reception }) {
  if (!sponsors || !sponsors.length) return null;
  return (
    <section style={{ background: c.cream, borderTop: `1px solid ${c.sand}`, padding: '48px 24px', textAlign: 'center' }}>
      {reception && (
        <p style={{ fontFamily: sans, fontSize: 15, color: c.brown, maxWidth: 620, margin: '0 auto 32px', lineHeight: 1.8 }}>
          {reception}
        </p>
      )}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: c.muted, marginBottom: 16 }}>
        Thanks to our sponsors
      </div>
      <div className="pg-sponsors" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 24px', fontFamily: sans, fontWeight: 700, letterSpacing: '-0.01em', color: c.orange }}>
        {sponsors.map((s) => <span key={s}>{s}</span>)}
      </div>
    </section>
  );
}

function Loading({ label = 'Loading…' }) {
  return (
    <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 2, color: c.muted, textAlign: 'center', padding: '120px 24px' }}>
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Index — the blog-style grid
// ---------------------------------------------------------------------------

function GalleryIndex() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/api/paws-gallery`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'We couldn’t load the gallery.');
        return d;
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <><Masthead /><div style={{ fontFamily: sans, color: c.terracotta, textAlign: 'center', padding: '100px 24px' }}>{error}</div></>;
  if (!data) return <><Masthead /><Loading /></>;

  const term = q.trim().toLowerCase();
  const entries = term
    ? data.entries.filter((e) =>
      (e.petName || '').toLowerCase().includes(term) || (e.ownerName || '').toLowerCase().includes(term))
    : data.entries;

  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead subtitle={`${data.sessionDate} · New Rochelle, NY`} />

      <div className="pg-layout">
        <main>
          {!data.entries.length && (
            <p style={{ fontFamily: sans, fontSize: 14, color: c.muted, lineHeight: 1.9 }}>
              The galleries go live soon — check back shortly.
            </p>
          )}
          {!!data.entries.length && !entries.length && (
            <p style={{ fontFamily: sans, fontSize: 14, color: c.muted }}>No pets match “{q}”.</p>
          )}
          <div className="pg-cards">
            {entries.map((e) => <Card key={e.slug} entry={e} price={data.priceCents} />)}
          </div>
        </main>

        <aside>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <div style={{ fontFamily: sans, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: c.ink, marginBottom: 12 }}>Welcome</div>
            <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.9, margin: 0 }}>
              Every pet from our {data.sessionDate} shoot days has a page here. Your favorite photo is
              free to download — the full set is {money(data.priceCents)}.
            </p>
          </div>

          <div style={{ marginBottom: 36 }}>
            <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: c.ink, textAlign: 'center', marginBottom: 12 }}>Find your pet</div>
            <input
              value={q}
              onChange={(ev) => setQ(ev.target.value)}
              placeholder="Pet or owner name"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontFamily: sans, fontSize: 14, color: c.ink, background: c.cream, border: `1px solid ${c.sand}` }}
            />
          </div>

          {/* Bought sessions leave the grid, so say where they went before
              somebody concludes their photos vanished. */}
          <p style={{ fontFamily: sans, fontSize: 13, color: c.muted, lineHeight: 1.9, borderTop: `1px solid ${c.sand}`, paddingTop: 20 }}>
            Already bought your set? Your page hasn’t moved — open the link from your
            confirmation email, or write to <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a>.
          </p>
          {data.reception && (
            <p style={{ fontFamily: sans, fontSize: 13, color: c.muted, lineHeight: 1.9 }}>
              {data.reception}
            </p>
          )}
        </aside>
      </div>

      <Sponsors sponsors={data.sponsors} reception={null} />
      <Footer />
    </div>
  );
}

function Card({ entry, price }) {
  const href = `/paws-gallery/${entry.slug}`;
  const go = (ev) => { ev.preventDefault(); window.history.pushState({}, '', href); window.dispatchEvent(new PopStateEvent('popstate')); };
  return (
    <article style={{ border: `1px solid ${c.sand}`, background: c.paper, display: 'flex', flexDirection: 'column' }}>
      <a href={href} onClick={go} style={{ display: 'block' }}>
        <img
          src={`${API_BASE}${entry.heroUrl}?w=800`}
          alt={entry.petName || 'Paws & Portraits'}
          loading="lazy"
          style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
        />
      </a>
      {/* Flex column with the caption growing, so the button sits on the same
          line across cards whether or not a pet has a caption. */}
      <div style={{ padding: '22px 20px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {entry.petName && (
          <a href={href} onClick={go} style={{ textDecoration: 'none' }}>
            <h2 style={{ fontFamily: sans, fontSize: 24, color: c.ink, margin: '0 0 8px', fontWeight: 700, letterSpacing: '-0.025em' }}>
              {entry.petName}
            </h2>
          </a>
        )}
        {entry.ownerName && (
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 14 }}>
            {entry.ownerName}
          </div>
        )}
        <div style={{ flex: 1 }}>
          {entry.story && (
            <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.9, margin: '0 0 18px' }}>
              {entry.story.length > 130 ? `${entry.story.slice(0, 130)}…` : entry.story}
            </p>
          )}
        </div>
        <a
          href={href}
          onClick={go}
          style={{ alignSelf: 'center', background: c.brown, color: c.cream, textDecoration: 'none', fontFamily: mono, fontSize: 10, letterSpacing: 2, padding: '11px 22px' }}
        >
          VIEW GALLERY
        </a>
        <div style={{ fontFamily: mono, fontSize: 10, color: c.muted, marginTop: 12 }}>
          1 free download · all photos {money(price)}
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Detail — one pet, free download + purchase
// ---------------------------------------------------------------------------

function TipPicker({ presets, tip, setTip, customTip, setCustomTip, label }) {
  const chip = (active) => ({
    fontFamily: mono, fontSize: 12, letterSpacing: 1, padding: '9px 16px', cursor: 'pointer',
    background: active ? c.orange : c.paper,
    color: active ? c.cream : c.brown,
    border: `1px solid ${active ? c.orange : c.sand}`,
  });
  const custom = customTip.trim() !== '';
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 10 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button type="button" onClick={() => { setTip(0); setCustomTip(''); }} style={chip(!custom && !tip)}>No thanks</button>
        {(presets || []).map((p) => (
          <button key={p} type="button" onClick={() => { setTip(p); setCustomTip(''); }} style={chip(!custom && tip === p)}>
            ${Math.round(p / 100)}
          </button>
        ))}
        <input
          value={customTip}
          onChange={(e) => setCustomTip(e.target.value)}
          placeholder="Other $"
          inputMode="decimal"
          style={{ width: 90, fontFamily: mono, fontSize: 12, padding: '9px 12px', textAlign: 'center', color: c.brown, background: c.paper, border: `1px solid ${custom ? c.orange : c.sand}` }}
        />
      </div>
    </div>
  );
}

function GalleryEntry({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [justPaid, setJustPaid] = useState(false);
  const [justTipped, setJustTipped] = useState(false);
  const [tip, setTip] = useState(0);        // cents; 0 = no tip
  const [customTip, setCustomTip] = useState('');
  const [dlEmail, setDlEmail] = useState('');
  const [dlError, setDlError] = useState('');

  const load = useCallback(() => {
    fetch(`${API_BASE}/api/paws-gallery/${encodeURIComponent(slug)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'We couldn’t find that gallery.');
        return d;
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [slug]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('paid')) setJustPaid(true);
    if (q.get('tipped')) setJustTipped(true);
    load();
    // Stripe's webhook can land just after the redirect — re-check briefly so
    // the page flips to "paid" without the visitor reloading.
    if (q.get('paid')) {
      const timers = [1500, 4000, 8000].map((ms) => setTimeout(load, ms));
      return () => timers.forEach(clearTimeout);
    }
  }, [load]);

  // A typed amount wins over a preset; blank or nonsense means no tip.
  const tipCents = customTip.trim()
    ? Math.round(parseFloat(customTip.replace(/[^0-9.]/g, '')) * 100) || 0
    : tip;

  const go = async (path, body) => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/api/paws-gallery/${encodeURIComponent(slug)}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not start checkout.');
      window.location.href = d.url;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const buy = () => go('checkout', { tipCents });
  const tipOnly = () => go('tip', { tipCents });

  if (error && !data) return <><Masthead /><div style={{ fontFamily: sans, color: c.terracotta, textAlign: 'center', padding: '100px 24px' }}>{error}</div></>;
  if (!data) return <><Masthead /><Loading /></>;

  const backToIndex = (ev) => {
    ev.preventDefault();
    window.history.pushState({}, '', '/paws-gallery');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead subtitle={`${data.ownerName ? `${data.ownerName} · ` : ''}${data.sessionDate}`} />

      <div className="pg-detail">
        <a href="/paws-gallery" onClick={backToIndex} style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: c.muted, textDecoration: 'none' }}>
          ← ALL GALLERIES
        </a>

        {data.petName && (
          <h2 className="pg-pet-name" style={{ fontFamily: sans, color: c.ink, margin: '24px 0 8px', fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, textAlign: 'center' }}>
            {data.petName}
          </h2>
        )}

        <img
          src={`${API_BASE}${data.heroUrl}?w=1600`}
          alt={data.petName || 'Paws & Portraits'}
          style={{ width: '100%', display: 'block', margin: '28px 0' }}
        />

        {data.story && (
          <p style={{ fontFamily: sans, fontSize: 16, color: c.brown, lineHeight: 1.75, textAlign: 'center', margin: '0 0 36px' }}>
            {data.story}
          </p>
        )}

        {/* Without a name the page has nothing to identify itself by, so give
            whoever recognises them a way to say so. */}
        {!data.petName && (
          <p style={{ fontFamily: sans, fontSize: 14, color: c.muted, lineHeight: 1.9, textAlign: 'center', margin: '0 0 36px' }}>
            We never caught this one’s name. If they’re yours, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a> and we’ll sort it out.
          </p>
        )}

        {/* Free download */}
        <div style={{ border: `1px solid ${c.sand}`, background: c.cream, padding: '28px 24px', textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 10 }}>
            Your free photo
          </div>
          <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.9, margin: '0 0 18px' }}>
            This one is yours — the original file, full resolution, no charge.
          </p>
          {/* The address is how a download becomes a claim — especially for the
              sessions that came back without a name on them. */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', maxWidth: 460, margin: '0 auto' }}>
            <input
              type="email"
              value={dlEmail}
              onChange={(e) => { setDlEmail(e.target.value); setDlError(''); }}
              placeholder="your@email.com"
              style={{ flex: '1 1 200px', minWidth: 0, boxSizing: 'border-box', padding: '13px 12px', fontFamily: sans, fontSize: 14, color: c.ink, background: c.paper, border: `1px solid ${dlError ? c.terracotta : c.sand}` }}
            />
            <button
              onClick={() => {
                const v = dlEmail.trim();
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setDlError('Please enter a valid email address.'); return; }
                window.location.href = `${API_BASE}${data.downloadUrl}?email=${encodeURIComponent(v)}`;
              }}
              style={{ background: c.orange, color: c.cream, border: 'none', cursor: 'pointer', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '14px 24px' }}
            >
              DOWNLOAD
            </button>
          </div>
          {dlError && (
            <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '10px 0 0' }}>{dlError}</p>
          )}
          <p style={{ fontFamily: sans, fontSize: 12, color: c.muted, margin: '10px 0 0' }}>
            So we know whose photo it is — we won't add you to anything.
          </p>
        </div>

        {/* Purchase */}
        <div style={{ border: `2px solid ${c.orange}`, background: c.paper, padding: '32px 24px', textAlign: 'center' }}>
          {data.paid ? (
            <>
              <div style={{ fontFamily: sans, fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', color: c.ink, marginBottom: 10 }}>You’re all set 🐾</div>
              <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.9, margin: '0 0 22px' }}>
                Your Google Drive link is on its way to your inbox. If it hasn’t arrived in a few
                minutes, check spam or email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a>.
              </p>
              {justTipped ? (
                <p style={{ fontFamily: sans, fontSize: 14, color: c.orange, margin: 0 }}>Thank you for the tip 💛</p>
              ) : (
                <div style={{ borderTop: `1px solid ${c.sand}`, paddingTop: 22 }}>
                  <TipPicker presets={data.tipPresets} tip={tip} setTip={setTip} customTip={customTip} setCustomTip={setCustomTip}
                    label="Want to leave a tip?" />
                  {error && <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '0 0 12px' }}>{error}</p>}
                  <button onClick={tipOnly} disabled={busy || !tipCents}
                    style={{ background: (busy || !tipCents) ? c.muted : c.brown, color: c.cream, border: 'none', cursor: (busy || !tipCents) ? 'default' : 'pointer', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '13px 28px' }}>
                    {busy ? 'STARTING CHECKOUT…' : tipCents ? `LEAVE A ${money(tipCents)} TIP` : 'LEAVE A TIP'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 8 }}>
                Every photo from the session
              </div>
              <div className="pg-price" style={{ fontFamily: mono, fontWeight: 700, color: c.orange, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums', lineHeight: 1, marginBottom: 14 }}>
                {money(data.priceCents)}
              </div>
              <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.9, margin: '0 0 22px' }}>
                The complete set from the session, full resolution. After checkout we’ll email you a
                Google Drive folder link — yours to keep and download whenever.
              </p>
              {justPaid && (
                <p style={{ fontFamily: sans, fontSize: 13, color: c.muted, margin: '0 0 16px' }}>
                  Payment received — finishing up, this page will update in a moment…
                </p>
              )}
              {error && (
                <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '0 0 16px' }}>{error}</p>
              )}
              <TipPicker presets={data.tipPresets} tip={tip} setTip={setTip} customTip={customTip} setCustomTip={setCustomTip}
                label="Add a tip for the photographer?" />
              <button
                onClick={buy}
                disabled={busy}
                style={{ background: busy ? c.muted : c.brown, color: c.cream, border: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '15px 34px' }}
              >
                {busy ? 'STARTING CHECKOUT…' : `BUY ALL PHOTOS — ${money(data.priceCents + tipCents)}`}
              </button>
              {tipCents > 0 && (
                <div style={{ fontFamily: mono, fontSize: 11, color: c.muted, marginTop: 10 }}>
                  {money(data.priceCents)} for the photos + {money(tipCents)} tip
                </div>
              )}
              {/* Most visitors only take the free photo — let them tip without
                  having to buy the set. */}
              {tipCents > 0 && (
                <div style={{ marginTop: 14 }}>
                  <button onClick={tipOnly} disabled={busy}
                    style={{ background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: sans, fontSize: 13, color: c.orange, textDecoration: 'underline' }}>
                    Or send just the {money(tipCents)} tip →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Sponsors sponsors={data.sponsors} reception={data.reception} />
      <Footer />
    </div>
  );
}

export default function PawsGallery({ slug }) {
  return slug ? <GalleryEntry slug={slug} /> : <GalleryIndex />;
}
